import { Image, RemoteSvg } from '@components/Elements/Image'
import { Colors } from '@vetahealth/fishing-gear/colors'
import React, { createContext, useContext } from 'react'
import { Text as NativeText, Platform, useWindowDimensions } from 'react-native'
import styled, { useTheme } from 'styled-components/native'
import { InlineLink, Text, TextSize, TextWeight } from '../Elements/Text'

// Heading

const levelToTextSize: Record<number, TextSize> = {
  1: 'xxLarge',
  2: 'heading2',
  3: 'xLarge',
  4: 'heading4',
  5: 'large',
  6: 'medium',
}

export const Heading = ({ children, level }: { children: any; level: number }) => {
  return (
    <Text size={levelToTextSize[level]} weight={level <= 3 ? 'bold' : 'semiBold'}>
      {children}
      {Platform.select({ native: '\n', web: '\n\n' })}
    </Text>
  )
}

function filterLinebreak(children: MarkdownNode): boolean {
  return children?.value !== '\n'
}

// Lists

const ListWrapper = styled.View`
  gap: 10px;
`
const ListItemWrapper = styled.View``
const ListElement = styled.View`
  flex-direction: row;
  gap: 10px;
`
const ListIndicator = styled.View`
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.tertiaryBackground};
  border-radius: 12px;
  margin-top: 0px;
`
const ListBullet = styled.View`
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
`
const ListTextWrapper = styled.View`
  flex: 1;
`

export type MarkdownNode = {
  type: 'element' | 'text'
  tagName?: string
  children?: MarkdownNode[]
  value?: string
  properties?: Record<string, any>
}

type MarkdownTextStyle = {
  weight?: TextWeight
  boldWeight?: TextWeight
  fontSize?: TextSize
  color?: Colors
  linkColor?: Colors
}

export type MarkdownProps = {
  text: string
  i18nEnabled?: boolean
  withoutParagraphSpacing?: boolean
  textStyle?: MarkdownTextStyle
  listStyle?: MarkdownTextStyle
  isCentered?: boolean
  customActions?: Record<string, () => void>
}

export const MarkdownContext = createContext<Pick<MarkdownProps, 'customActions' | 'textStyle' | 'listStyle'>>({})

function InnerListItem({ node, format = [] }: { node: MarkdownNode; format?: string[] }): React.ReactNode {
  const { customActions, listStyle } = useContext(MarkdownContext)

  return (
    <>
      {node?.children?.filter(filterLinebreak).map((innerListNode, i: number) => {
        if (innerListNode.type === 'element') {
          // filter sub lists (breaking layout on mobile: <Text><View/></Text> => View loses positioning relation)
          // TODO: find a way to have inline styling and sub lists
          if (['ol', 'ul'].includes(innerListNode?.tagName as string)) {
            // return <List key={i} node={innerListNode} />
            return null
          }

          // for defined tags loop another level
          if (innerListNode?.tagName && ['p', 'em', 'strong', 'a'].includes(innerListNode.tagName)) {
            return <InnerListItem key={i} node={innerListNode} format={[...format, innerListNode.tagName]} />
          }
        }

        // render actual text with styling
        if (innerListNode.type === 'text') {
          if (format.includes('a') && node.properties?.href) {
            return (
              <InlineLink
                key={i}
                onPress={customActions?.[node.properties.href]}
                href={node.properties.href}
                color={listStyle?.linkColor}
              >
                {innerListNode?.value}
              </InlineLink>
            )
          }

          return (
            <Text
              size={listStyle?.fontSize}
              weight={format.includes('strong') ? listStyle?.boldWeight : listStyle?.weight}
              isItalic={format.includes('em')}
              color={listStyle?.color}
              key={i}
            >
              {innerListNode?.value}
            </Text>
          )
        }

        return null
      })}
    </>
  )
}

function ListItem({
  node,
  isOrdered,
  index,
}: {
  node: MarkdownNode
  isOrdered: boolean
  index: number
}): React.ReactNode {
  const theme = useTheme()

  return (
    <ListItemWrapper>
      <ListElement>
        <ListIndicator>
          {isOrdered ? (
            <Text weight="bold" size="settings" color={theme.colors.primary}>
              {index + 1}
            </Text>
          ) : (
            <ListBullet />
          )}
        </ListIndicator>
        <ListTextWrapper>
          <NativeText>
            <InnerListItem node={node} />
          </NativeText>
        </ListTextWrapper>
      </ListElement>
    </ListItemWrapper>
  )
}

export function List({ node }: { node: MarkdownNode }): React.ReactNode {
  return (
    <ListWrapper>
      {node?.children
        ?.filter(({ tagName }) => tagName === 'li')
        .map((listNode, i: number) => {
          return <ListItem key={i} index={i} node={listNode} isOrdered={node?.tagName === 'ol'} />
        })}
    </ListWrapper>
  )
}

// Table

const TableWrapper = styled.View`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.small}px;
  overflow: hidden;
  margin-bottom: 20px;
  background-color: ${({ theme }) => theme.colors.secondaryBackground};
`
const TableRowWrapper = styled.View<{ color?: Colors }>`
  width: 100%;
  flex-direction: row;
  ${({ color }) => color && `background-color: ${color};`}
`
const TableCell = styled.View`
  flex: 1;
  padding: 5px 10px 8px;
  border-color: ${({ theme }) => theme.colors.background};
  border-left-width: 1px;
  margin-left: -1px;
  border-top-width: 1px;
  margin-top: -1px;
`

function TableRow({ node, isHeader }: { node: MarkdownNode; isHeader?: boolean }): React.ReactNode {
  return (
    <>
      {node?.value && (
        <TableCell>
          <Text weight={isHeader ? 'semiBold' : 'medium'} size="small" color={isHeader ? Colors.white : undefined}>
            {node.value}
          </Text>
        </TableCell>
      )}
      {node?.children?.filter(filterLinebreak).map((rowNode, index: number) => (
        <TableRow key={index} node={rowNode} isHeader={isHeader} />
      ))}
    </>
  )
}

export function Table({ node }: { node: MarkdownNode }): React.ReactNode | null {
  const theme = useTheme()
  const header = node.children?.find((node) => node.tagName === 'thead')
  const body = node?.children?.find((node) => node.tagName === 'tbody')

  if (!header || !body) return null

  return (
    <TableWrapper>
      <TableRowWrapper color={theme.colors.primary}>
        <TableRow node={header} isHeader />
      </TableRowWrapper>
      {body?.children?.filter(filterLinebreak).map((bodyNode, index: number) => (
        <TableRowWrapper key={index}>
          <TableRow node={bodyNode} />
        </TableRowWrapper>
      ))}
    </TableWrapper>
  )
}

const ImageWrapper = styled.View`
  flex-direction: row;
  margin: 10px 0 0;
`

export function InlineImage({ node }: { node: MarkdownNode }): React.ReactNode | null {
  const { width } = useWindowDimensions()
  const theme = useTheme()

  if (!node.properties?.src) return null

  return (
    <ImageWrapper>
      {width > 0 && node.properties.src.endsWith('.svg') ? (
        <RemoteSvg uri={node.properties.src.srcUrl} />
      ) : (
        <Image
          isZoomable
          uri={`${node.properties.src}?w=${(width < theme.maxWidth ? width : theme.maxWidth) * 2}`}
          caption={node.properties?.alt}
        />
      )}
    </ImageWrapper>
  )
}
